import Layout from "@/components/Layout";
import PermissionDenied from "@/components/PermissionDenied";
import { GridPaginationFooter } from "@/components/catalog/GridPaginationControls";
import { DatePartsPicker } from "@/components/hr/DatePartsPicker";
import { HrListToolbar, type HrQuickDatePreset } from "@/components/hr/HrListToolbar";
import { ConfirmAlertDialog } from "@/components/ConfirmAlertDialog";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/sonner";
import { useGridPagination } from "@/hooks/useGridPagination";
import {
  approveLeaveRequest,
  cancelLeaveRequest,
  createLeaveRequest,
  leaveRequestErrorMessage,
  listLeaveRequests,
  rejectLeaveRequest,
  syncLeaveRequestsFromApi,
  type LeaveRequestRecord,
  type LeaveRequestStatus,
} from "@/lib/hr/leaveRequestsStore";
import { getLeaveTypeById, listActiveLeaveTypeRecords, syncLeaveTypesFromApi } from "@/lib/hr/leaveTypesStore";
import { listEmployees, syncEmployeesFromApi, type EmployeeRecord } from "@/lib/hr/employeesStore";
import { getActiveLeaveYear, syncLeaveYearsFromApi } from "@/lib/hr/leaveYearsStore";
import { can, getStoredAuthUser } from "@/lib/authUser";
import { cn } from "@/lib/utils";
import { isAxiosError } from "axios";
import { endOfDay, format, isValid, parseISO, startOfDay, startOfMonth, subDays } from "date-fns";
import { CalendarIcon, CalendarRange, Check, ChevronsUpDown, Loader2, Plus, X, XCircle } from "lucide-react";
import { motion } from "framer-motion";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import type { AuthUser } from "@/services/authService";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";

const PAGE_SIZE_OPTIONS = [20, 24, 28, 32, 40, 48, 60] as const;

const STATUSES: LeaveRequestStatus[] = ["pending", "approved", "rejected", "cancelled"];

type ReqSortBy = "latest" | "oldest" | "employee_asc" | "employee_desc" | "start_desc" | "start_asc";

const CREATE_EMPLOYEE_NONE = "__none_employee__";
const CREATE_LEAVE_TYPE_NONE = "__none_leave_type__";

function employeeLabel(e: EmployeeRecord): string {
  const n = (e.fullName || `${e.firstName} ${e.lastName}`).trim() || "—";
  return e.employeeCode ? `${e.employeeCode} · ${n}` : n;
}

function statusBadge(status: LeaveRequestStatus) {
  switch (status) {
    case "approved":
      return { label: "Approved", cls: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400" };
    case "rejected":
      return { label: "Rejected", cls: "bg-destructive/15 text-destructive" };
    case "cancelled":
      return { label: "Cancelled", cls: "bg-muted text-muted-foreground" };
    default:
      return { label: "Pending", cls: "bg-amber-500/15 text-amber-800 dark:text-amber-400" };
  }
}

export default function LeaveRequests() {
  const [searchParams] = useSearchParams();
  const [user, setUser] = useState(() => getStoredAuthUser());
  const canCreate = can(user, "leave_request_can_create");
  const canApprove = can(user, "leave_request_can_approve");
  const canReject = can(user, "leave_request_can_reject");
  const canCancel = can(user, "leave_request_can_cancel");
  const [isForbidden, setIsForbidden] = useState(false);

  const [version, setVersion] = useState(0);
  const refresh = useCallback(() => setVersion((v) => v + 1), []);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sortBy, setSortBy] = useState<ReqSortBy>("latest");
  const [quickDate, setQuickDate] = useState<HrQuickDatePreset>("all");
  const [filterCreatedDate, setFilterCreatedDate] = useState<Date | undefined>(undefined);
  const [viewMode, setViewMode] = useState<"grid" | "list">("list");
  const [filterEmployee, setFilterEmployee] = useState("__all__");
  const [filterStatus, setFilterStatus] = useState<string>("__all__");
  const [filterPaid, setFilterPaid] = useState<string>("__all__");

  const [employees, setEmployees] = useState<EmployeeRecord[]>([]);
  const [createOpenState, setCreateOpenState] = useState(false);

  const [cemployeeId, setCemployeeId] = useState(CREATE_EMPLOYEE_NONE);
  const [employeePickerOpen, setEmployeePickerOpen] = useState(false);
  const [employeePickerCycle, setEmployeePickerCycle] = useState(0);
  const [cleaveTypeId, setCleaveTypeId] = useState(CREATE_LEAVE_TYPE_NONE);
  const [cisPaid, setCisPaid] = useState(true);
  const [cstart, setCstart] = useState("");
  const [cend, setCend] = useState("");
  const [creason, setCreason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [approveId, setApproveId] = useState<string | null>(null);
  const [rejectRow, setRejectRow] = useState<LeaveRequestRecord | null>(null);
  const [rejectNote, setRejectNote] = useState("");

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === "auth_user") setUser(getStoredAuthUser());
    };
    const onAuth = () => setUser(getStoredAuthUser());
    window.addEventListener("storage", onStorage);
    window.addEventListener("auth_user_updated", onAuth);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("auth_user_updated", onAuth);
    };
  }, []);

  useEffect(() => {
    const pre = searchParams.get("employee_id");
    if (pre) setFilterEmployee(pre);
  }, [searchParams]);

  useEffect(() => {
    let m = true;
    void (async () => {
      setLoading(true);
      setIsForbidden(false);
      try {
        await Promise.all([
          syncLeaveRequestsFromApi(),
          syncLeaveTypesFromApi(),
          syncEmployeesFromApi(),
          syncLeaveYearsFromApi(),
        ]);
        if (m) {
          setEmployees(listEmployees());
          refresh();
        }
      } catch (e) {
        if (m && isAxiosError(e) && e.response?.status === 403) {
          setIsForbidden(true);
          if (m) setLoading(false);
          return;
        }
        console.error(e);
        if (m) toast.error(leaveRequestErrorMessage(e));
      } finally {
        if (m) setLoading(false);
      }
    })();
    return () => {
      m = false;
    };
  }, [refresh]);

  useEffect(() => {
    setEmployees(listEmployees());
  }, [version]);

  const leaveTypes = useMemo(() => listActiveLeaveTypeRecords(), [version]);
  const requests = useMemo(() => listLeaveRequests(), [version]);
  const activeLeaveYear = useMemo(() => getActiveLeaveYear(), [version]);

  const selectedEmployee = useMemo(
    () => (cemployeeId && cemployeeId !== CREATE_EMPLOYEE_NONE ? employees.find((e) => e.id === cemployeeId) ?? null : null),
    [cemployeeId, employees],
  );

  const selectedAllocation = useMemo(() => {
    if (!selectedEmployee || !activeLeaveYear?.id || !cleaveTypeId || cleaveTypeId === CREATE_LEAVE_TYPE_NONE) return null;
    const yearId = String(activeLeaveYear.id);
    return (
      selectedEmployee.leaveAllocations.find(
        (a) => String(a.leaveYearId) === yearId && String(a.leaveTypeId) === String(cleaveTypeId),
      ) ?? null
    );
  }, [activeLeaveYear?.id, cleaveTypeId, selectedEmployee]);

  const leaveTypeTitleFor = useCallback(
    (r: LeaveRequestRecord): string => {
      const fromEmbedded = r.leaveType?.title?.trim();
      if (fromEmbedded) return fromEmbedded;
      const fromStore = getLeaveTypeById(r.leaveTypeId)?.title?.trim();
      return fromStore || "—";
    },
    [version],
  );

  const currentUserRole = useMemo(() => {
    try {
      const raw = localStorage.getItem("auth_user");
      if (!raw) return "user";
      const u = JSON.parse(raw) as Partial<AuthUser>;
      return (u.role ?? "user").toString().toLowerCase();
    } catch {
      return "user";
    }
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return requests.filter((r) => {
      if (filterEmployee !== "__all__" && r.employeeId !== filterEmployee) return false;
      if (filterStatus !== "__all__" && r.status !== filterStatus) return false;
      if (filterPaid === "paid" && !r.isPaid) return false;
      if (filterPaid === "unpaid" && r.isPaid) return false;
      if (filterCreatedDate) {
        const want = format(filterCreatedDate, "yyyy-MM-dd");
        const created = parseISO(r.createdAt);
        const got = isValid(created) ? format(created, "yyyy-MM-dd") : null;
        if (got !== want) return false;
      }
      if (quickDate !== "all") {
        const created = parseISO(r.createdAt);
        if (!isValid(created)) return false;
        const now = new Date();
        const end = endOfDay(now);
        const start =
          quickDate === "today" ? startOfDay(now) : quickDate === "week" ? startOfDay(subDays(now, 6)) : startOfMonth(now);
        if (created < start || created > end) return false;
      }
      if (!q) return true;
      const en = (r.employee?.fullName ?? "").toLowerCase();
      const ec = (r.employee?.employeeCode ?? "").toLowerCase();
      const lt = leaveTypeTitleFor(r).toLowerCase();
      return en.includes(q) || ec.includes(q) || lt.includes(q) || (r.reason ?? "").toLowerCase().includes(q);
    });
  }, [requests, query, filterEmployee, filterStatus, filterPaid, filterCreatedDate, quickDate, leaveTypeTitleFor]);

  const sorted = useMemo(() => {
    const items = [...filtered];
    items.sort((a, b) => {
      const emp = (x: LeaveRequestRecord) => (x.employee?.fullName ?? x.employee?.employeeCode ?? "").toLowerCase();
      switch (sortBy) {
        case "oldest":
          return a.createdAt.localeCompare(b.createdAt);
        case "employee_desc":
          return emp(b).localeCompare(emp(a));
        case "employee_asc":
          return emp(a).localeCompare(emp(b));
        case "start_asc":
          return a.startDate.localeCompare(b.startDate);
        case "start_desc":
          return b.startDate.localeCompare(a.startDate);
        case "latest":
        default:
          return b.createdAt.localeCompare(a.createdAt);
      }
    });
    return items;
  }, [filtered, sortBy]);

  const appliedFiltersCount =
    (filterEmployee !== "__all__" ? 1 : 0) +
    (filterStatus !== "__all__" ? 1 : 0) +
    (filterPaid !== "__all__" ? 1 : 0) +
    (filterCreatedDate ? 1 : 0);

  const resetSheetFilters = () => {
    setFilterEmployee("__all__");
    setFilterStatus("__all__");
    setFilterPaid("__all__");
    setFilterCreatedDate(undefined);
  };

  const paginationKey = `${query}|${sortBy}|${quickDate}|${filterCreatedDate?.getTime() ?? ""}|${filterEmployee}|${filterStatus}|${filterPaid}`;

  const {
    page,
    setPage,
    pageSize,
    setPageSize,
    totalCount,
    totalPages,
    paginatedItems: paginated,
    rangeStart,
    rangeEnd,
  } = useGridPagination(sorted, paginationKey, {
    pageSizeOptions: PAGE_SIZE_OPTIONS,
    defaultPageSize: PAGE_SIZE_OPTIONS[0],
  });

  const openCreate = () => {
    setCemployeeId(CREATE_EMPLOYEE_NONE);
    setEmployeePickerOpen(false);
    setCleaveTypeId(CREATE_LEAVE_TYPE_NONE);
    setCisPaid(true);
    setCstart("");
    setCend("");
    setCreason("");
    setCreateOpenState(true);
  };

  const submitCreate = async () => {
    if (cemployeeId === CREATE_EMPLOYEE_NONE || cleaveTypeId === CREATE_LEAVE_TYPE_NONE || !cstart || !cend) {
      toast.error("Employee, leave type, start and end dates are required.");
      return;
    }
    setSubmitting(true);
    try {
      await createLeaveRequest({
        employeeId: cemployeeId,
        leaveTypeId: cleaveTypeId,
        startDate: cstart,
        endDate: cend,
        isPaid: cisPaid,
        reason: creason.trim() || null,
      });
      toast.success("Leave request submitted.");
      setCreateOpenState(false);
      await syncLeaveRequestsFromApi();
      refresh();
    } catch (e) {
      console.error(e);
      toast.error(leaveRequestErrorMessage(e));
    } finally {
      setSubmitting(false);
    }
  };

  const doApprove = async () => {
    if (!approveId) return;
    try {
      await approveLeaveRequest(approveId);
      toast.success("Leave approved.");
      setApproveId(null);
      await syncLeaveRequestsFromApi();
      refresh();
    } catch (e) {
      console.error(e);
      toast.error(leaveRequestErrorMessage(e));
    }
  };

  const doReject = async () => {
    if (!rejectRow) return;
    try {
      await rejectLeaveRequest(rejectRow.id, rejectNote);
      toast.success("Leave rejected.");
      setRejectRow(null);
      setRejectNote("");
      await syncLeaveRequestsFromApi();
      refresh();
    } catch (e) {
      console.error(e);
      toast.error(leaveRequestErrorMessage(e));
    }
  };

  const doCancel = async (id: string) => {
    try {
      await cancelLeaveRequest(id);
      toast.success("Request cancelled.");
      await syncLeaveRequestsFromApi();
      refresh();
    } catch (e) {
      console.error(e);
      toast.error(leaveRequestErrorMessage(e));
    }
  };

  const renderRequestCard = (r: LeaveRequestRecord) => {
    const b = statusBadge(r.status);
    const empName = r.employee?.fullName ?? "—";
    const lt = leaveTypeTitleFor(r);
    return (
      <div className="glass-card p-4 flex flex-col lg:flex-row lg:items-center gap-4 h-full">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <div className="shrink-0 h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <CalendarRange className="w-5 h-5 text-primary" />
          </div>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <Link to={`/employees/${r.employeeId}`} className="font-semibold text-foreground hover:underline truncate">
                {empName}
              </Link>
              <span className={cn("text-[10px] px-2 py-0.5 rounded-full font-medium", b.cls)}>{b.label}</span>
              <span
                className={cn(
                  "text-[10px] px-2 py-0.5 rounded-full font-medium",
                  r.isPaid
                    ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400"
                    : "bg-amber-500/15 text-amber-800 dark:text-amber-400",
                )}
              >
                {r.isPaid ? "Paid" : "Unpaid"}
              </span>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              {lt} · {r.startDate} → {r.endDate} · {r.days} day(s)
            </p>
            {r.reason ? <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{r.reason}</p> : null}
            {r.status !== "pending" && r.reviewNote ? <p className="text-xs text-muted-foreground mt-1">Note: {r.reviewNote}</p> : null}
            <p className="text-[10px] text-muted-foreground mt-2">
              Requested {format(parseISO(r.createdAt), "PPp")}
              {r.reviewer?.name ? ` · Reviewed by ${r.reviewer.name}` : ""}
            </p>
          </div>
        </div>
        {r.status === "pending" ? (
          <div className="flex flex-wrap gap-2 shrink-0">
            {canApprove ? (
              <Button type="button" variant="glass" size="sm" className="gap-1 h-8 text-xs" onClick={() => setApproveId(r.id)}>
                <Check className="w-3.5 h-3.5" /> Approve
              </Button>
            ) : null}
            {canReject ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-1 h-8 text-xs border-destructive/40 text-destructive"
                onClick={() => setRejectRow(r)}
              >
                <XCircle className="w-3.5 h-3.5" /> Reject
              </Button>
            ) : null}
            {canCancel ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 text-xs text-muted-foreground"
                onClick={() => void doCancel(r.id)}
              >
                <X className="w-3.5 h-3.5" /> Cancel
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>
    );
  };

  if (isForbidden) {
    return (
      <PermissionDenied
        title="Leave requests access required"
        message="You don’t have permission to view leave requests. Ask an administrator to grant leave_request_can_view_any."
      />
    );
  }

  return (
    <Layout title="Leave requests">
      <div className="space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-display font-bold text-foreground">Leave requests</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Submit and approve staff leave.{" "}
              <Link to="/leave-types" className="text-primary font-medium hover:underline">
                Leave types
              </Link>
            </p>
          </div>
          {canCreate ? (
            <Button variant="premium" className="gap-2" onClick={openCreate}>
              <Plus className="w-4 h-4" /> New request
            </Button>
          ) : null}
        </div>

        <HrListToolbar
          searchPlaceholder="Search by name or description…"
          query={query}
          onQueryChange={setQuery}
          filtersOpen={filtersOpen}
          onFiltersOpenChange={setFiltersOpen}
          appliedFiltersCount={appliedFiltersCount}
          filterSheetTitle="Filter leave requests"
          filterSheetDescription="Refine by employee, status, paid/unpaid, and request date."
          onResetSheetFilters={resetSheetFilters}
          pageSize={pageSize}
          onPageSizeChange={setPageSize}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          pageSizeIdPrefix="leave-requests"
          sortBy={sortBy}
          onSortByChange={(v) => setSortBy(v as ReqSortBy)}
          sortOptions={[
            { value: "latest", label: "Newest first" },
            { value: "oldest", label: "Oldest first" },
            { value: "employee_asc", label: "Employee: A to Z" },
            { value: "employee_desc", label: "Employee: Z to A" },
            { value: "start_desc", label: "Start date: latest first" },
            { value: "start_asc", label: "Start date: earliest first" },
          ]}
          quickDate={quickDate}
          onQuickDateChange={setQuickDate}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          filterSheetChildren={
            <div className="rounded-xl border border-border/50 bg-muted/15 p-4 space-y-4">
              <p className="text-sm font-medium text-foreground">Request</p>
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Employee</Label>
                <Select value={filterEmployee} onValueChange={setFilterEmployee}>
                  <SelectTrigger className="glass-input h-10">
                    <SelectValue placeholder="All employees" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All employees</SelectItem>
                    {employees.map((e) => (
                      <SelectItem key={e.id} value={e.id}>
                        {employeeLabel(e)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Status</Label>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="glass-input h-10">
                    <SelectValue placeholder="All statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All statuses</SelectItem>
                    {STATUSES.map((s) => (
                      <SelectItem key={s} value={s}>
                        {s.charAt(0).toUpperCase() + s.slice(1)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Paid / Unpaid</Label>
                <Select value={filterPaid} onValueChange={setFilterPaid}>
                  <SelectTrigger className="glass-input h-10">
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                    <SelectItem value="unpaid">Unpaid</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Created date</Label>
                <Popover modal={true}>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      className={cn("glass-input h-10 w-full justify-start text-left font-normal", !filterCreatedDate && "text-muted-foreground")}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4 shrink-0 opacity-70" />
                      {filterCreatedDate ? format(filterCreatedDate, "PPP") : "Any date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="z-[100] w-auto p-0" align="start" sideOffset={6}>
                    <Calendar mode="single" selected={filterCreatedDate} onSelect={setFilterCreatedDate} initialFocus />
                    <div className="flex gap-2 border-t p-2">
                      <Button type="button" variant="ghost" size="sm" className="flex-1" onClick={() => setFilterCreatedDate(undefined)}>
                        Clear
                      </Button>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          }
        />

        {loading ? (
          <div className="glass-card p-8 flex items-center gap-2 text-muted-foreground text-sm">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : sorted.length === 0 ? (
          <div className="glass-card p-6 text-sm text-muted-foreground">
            {requests.length === 0 ? "No leave requests yet. Create one to get started." : "No leave requests match your search."}
          </div>
        ) : viewMode === "grid" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {paginated.map((r, i) => (
              <motion.div key={r.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.02 }}>
                {renderRequestCard(r)}
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            {paginated.map((r) => (
              <div key={r.id}>{renderRequestCard(r)}</div>
            ))}
          </div>
        )}

        {sorted.length > 0 ? (
          <GridPaginationFooter
            totalCount={totalCount}
            rangeStart={rangeStart}
            rangeEnd={rangeEnd}
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        ) : null}

        <Dialog open={createOpenState} onOpenChange={setCreateOpenState}>
          <DialogContent className="!flex max-h-[min(85vh,640px)] w-[92vw] max-w-xl flex-col gap-0 overflow-hidden p-0">
            <DialogHeader className="shrink-0 border-b border-border/50 px-4 py-2.5 pr-12">
              <DialogTitle className="text-left text-lg font-display">New leave request</DialogTitle>
            </DialogHeader>

            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-3">
              <div className="space-y-2.5">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Employee</Label>
                  <Popover
                    modal
                    open={employeePickerOpen}
                    onOpenChange={(next) => {
                      setEmployeePickerOpen(next);
                      if (next) setEmployeePickerCycle((c) => c + 1);
                    }}
                  >
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        role="combobox"
                        aria-expanded={employeePickerOpen}
                        className={cn(
                          "glass-input h-9 w-full justify-between px-3 font-normal",
                          (!cemployeeId || cemployeeId === CREATE_EMPLOYEE_NONE) && "text-muted-foreground",
                        )}
                      >
                        <span className="truncate text-left">
                          {selectedEmployee ? employeeLabel(selectedEmployee) : "Search employee…"}
                        </span>
                        <ChevronsUpDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="z-[100] w-[var(--radix-popover-trigger-width)] p-0" align="start" sideOffset={4}>
                      <Command key={employeePickerCycle} shouldFilter>
                        <CommandInput placeholder="Search name or code…" className="h-9" />
                        <CommandList>
                          <CommandEmpty>No employee found.</CommandEmpty>
                          <CommandGroup>
                            {employees.map((e) => {
                              const label = employeeLabel(e);
                              const selected = cemployeeId === e.id;
                              return (
                                <CommandItem
                                  key={e.id}
                                  value={`${label} ${e.employeeCode ?? ""} ${e.id}`}
                                  onSelect={() => {
                                    setCemployeeId(e.id);
                                    setEmployeePickerOpen(false);
                                  }}
                                  className="gap-2"
                                >
                                  <Check className={cn("h-3.5 w-3.5 shrink-0", selected ? "opacity-100" : "opacity-0")} />
                                  <span className="truncate">{label}</span>
                                </CommandItem>
                              );
                            })}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Leave type</Label>
                    <Select value={cleaveTypeId} onValueChange={setCleaveTypeId}>
                      <SelectTrigger className="glass-input h-9">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={CREATE_LEAVE_TYPE_NONE}>Select…</SelectItem>
                        {leaveTypes.map((t) => (
                          <SelectItem key={t.id} value={t.id}>
                            {t.title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Paid / Unpaid</Label>
                    <Select value={cisPaid ? "paid" : "unpaid"} onValueChange={(v) => setCisPaid(v === "paid")}>
                      <SelectTrigger className="glass-input h-9">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="paid">Paid</SelectItem>
                        <SelectItem value="unpaid">Unpaid</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">
                      Start date <span className="text-destructive">*</span>
                    </Label>
                    <DatePartsPicker
                      value={cstart}
                      onChange={setCstart}
                      fromYear={1980}
                      toYear={new Date().getFullYear() + 5}
                      placeholder="Start date"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">
                      End date <span className="text-destructive">*</span>
                    </Label>
                    <DatePartsPicker
                      value={cend}
                      onChange={setCend}
                      fromYear={1980}
                      toYear={new Date().getFullYear() + 5}
                      placeholder="End date"
                    />
                  </div>
                </div>

                <div className="rounded-lg border border-border/50 bg-muted/10 p-2.5 space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs font-normal text-foreground">Balance</p>
                    {activeLeaveYear ? (
                      <p className="text-[10px] text-muted-foreground truncate">
                        {activeLeaveYear.label}
                      </p>
                    ) : null}
                  </div>

                  {!selectedEmployee ? (
                    <p className="text-xs text-muted-foreground">Select an employee.</p>
                  ) : !selectedEmployee.leavePolicyId?.trim() ? (
                    <p className="text-xs text-destructive">No leave policy assigned for this period.</p>
                  ) : !cleaveTypeId || cleaveTypeId === CREATE_LEAVE_TYPE_NONE ? (
                    <p className="text-xs text-muted-foreground">Select a leave type.</p>
                  ) : (
                    <div className="grid grid-cols-3 gap-1.5">
                      <div className="rounded-md border border-sky-500/20 bg-sky-500/10 px-2 py-1.5 text-center">
                        <p className="text-[10px] text-sky-700 dark:text-sky-400">Allocated</p>
                        <p className="text-sm tabular-nums text-foreground">
                          {selectedAllocation ? selectedAllocation.totalDays : "—"}
                        </p>
                      </div>
                      <div className="rounded-md border border-destructive/20 bg-destructive/10 px-2 py-1.5 text-center">
                        <p className="text-[10px] text-destructive">Used</p>
                        <p className="text-sm tabular-nums text-foreground">
                          {selectedAllocation ? selectedAllocation.usedDays.toFixed(2) : "—"}
                        </p>
                      </div>
                      <div className="rounded-md border border-emerald-500/20 bg-emerald-500/10 px-2 py-1.5 text-center">
                        <p className="text-[10px] text-emerald-700 dark:text-emerald-400">Remaining</p>
                        <p className="text-sm tabular-nums text-foreground">
                          {selectedAllocation ? selectedAllocation.remainingDays.toFixed(2) : "—"}
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Reason</Label>
                  <Textarea
                    className="glass-input min-h-[56px] text-sm"
                    value={creason}
                    onChange={(e) => setCreason(e.target.value)}
                    placeholder="Optional"
                  />
                </div>
              </div>
            </div>

            <DialogFooter className="shrink-0 border-t border-border/50 px-4 py-2.5">
              <Button variant="glass" onClick={() => setCreateOpenState(false)} disabled={submitting}>
                Cancel
              </Button>
              <Button variant="premium" onClick={() => void submitCreate()} disabled={submitting}>
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={!!rejectRow} onOpenChange={(o) => !o && setRejectRow(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Reject leave request</DialogTitle>
            </DialogHeader>
            <Textarea
              className="glass-input min-h-[88px]"
              placeholder="Optional note to employee…"
              value={rejectNote}
              onChange={(e) => setRejectNote(e.target.value)}
            />
            <DialogFooter>
              <Button variant="glass" onClick={() => setRejectRow(null)}>
                Close
              </Button>
              <Button variant="destructive" onClick={() => void doReject()}>
                Reject
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <ConfirmAlertDialog
          open={approveId !== null}
          onOpenChange={(o) => !o && setApproveId(null)}
          title="Approve this leave request?"
          description="The employee will be marked as approved for this date range. Ensure no scheduling conflicts."
          mode="confirm"
          variant="default"
          confirmLabel="Approve"
          cancelLabel="Back"
          onConfirm={() => void doApprove()}
        />
      </div>
    </Layout>
  );
}
